// One-shot push-notification setup for SMRT Tasks. Designed to be invoked
// after the session resolves on a protected route — see AuthRedirect.
//
// Responsibilities:
//   1. Bail out on web / non-native platforms (the plugin throws otherwise).
//   2. Request OS permission once. If denied, silently no-op.
//   3. Trigger APNs/FCM registration via the Capacitor plugin.
//   4. Receive the FCM token from the native side. The trigger differs per
//      platform but both paths feed the same `registerToken()` so backend
//      ingestion stays identical:
//        • iOS — custom DOM event ("asksmart-fcm-token") posted by
//          AppDelegate.swift's MessagingDelegate. We do NOT use the plugin's
//          own `registration` event on iOS: Firebase Messaging's swizzle of
//          the APNs callback runs first and the plugin's event never fires.
//        • Android — the plugin's standard `registration` event. No swizzle
//          conflict exists on Android, so the plugin path is reliable and
//          adding a custom native bridge would be unnecessary maintenance.
//   5. On a notification tap, navigate to /smrt-tasks.
//
// Idempotent: calling the hook again on the same mount won't re-register or
// re-prompt — the plugin's internal state suppresses duplicate prompts, and
// the backend POST is upsert-safe (sha256 of the token is the doc id).

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

const TASKS_ROUTE = "/smrt-tasks";

interface FcmTokenEvent extends CustomEvent {
  detail: { token: string };
}

export function useFcmRegistration(enabled: boolean): void {
  const navigate = useNavigate();
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (startedRef.current) return;
    if (!Capacitor.isNativePlatform()) return;
    startedRef.current = true;

    let cleanup: (() => void) | null = null;

    const setup = async () => {
      try {
        const { PushNotifications } = await import(
          "@capacitor/push-notifications"
        );

        console.log(`[fcm] setup starting platform=${Capacitor.getPlatform()}`);

        const perm = await PushNotifications.checkPermissions();
        console.log(`[fcm] checkPermissions → receive=${perm.receive}`);
        let granted = perm.receive === "granted";
        if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
          console.log("[fcm] requesting permissions");
          const req = await PushNotifications.requestPermissions();
          console.log(`[fcm] requestPermissions → receive=${req.receive}`);
          granted = req.receive === "granted";
        }
        if (!granted) {
          console.log("[fcm] permission denied — push disabled");
          return;
        }
        console.log("[fcm] permission granted");

        // FCM token bridge — see header comment for why this isn't the
        // plugin's `registration` event. The native side both:
        //   (a) Stashes the token at window.__asksmartFcmToken for the
        //       cold-start case (token often arrives BEFORE React mounts).
        //   (b) Dispatches an `asksmart-fcm-token` event for live updates.
        // We handle both — read the stash on attach, then listen for events.
        // The id we get back (sha256 of the token) is stashed in localStorage
        // so logout can DELETE it without re-deriving the hash on the device.
        const registerToken = async (token: string) => {
          const platform =
            Capacitor.getPlatform() === "android" ? "android" : "ios";
          console.log(
            `[fcm] registerToken called platform=${platform} tokenLen=${token?.length ?? 0}`,
          );
          if (!token) {
            console.warn("[fcm] registerToken aborted — empty token");
            return;
          }
          try {
            const res = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/device-tokens`,
              {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, platform }),
              },
            );
            console.log(`[fcm] /device-tokens POST → status=${res.status}`);
            if (res.ok) {
              const data = await res.json();
              if (data?.id) {
                try {
                  localStorage.setItem("asksmart.deviceTokenId", data.id);
                } catch {
                  // private mode / quota — best effort
                }
              }
            } else {
              console.warn("[fcm] token register failed:", res.status);
            }
          } catch (err) {
            console.warn("[fcm] token register error:", err);
          }
        };

        const onFcmToken = (e: Event) => {
          const event = e as FcmTokenEvent;
          void registerToken(event?.detail?.token || "");
        };
        window.addEventListener("asksmart-fcm-token", onFcmToken);

        // Cold-start replay: native may have stashed the token before this
        // listener was attached (Firebase often delivers a cached FCM token
        // synchronously at app launch).
        const stashed = (window as unknown as { __asksmartFcmToken?: string })
          .__asksmartFcmToken;
        if (stashed) {
          void registerToken(stashed);
        }

        // Android-only: plugin's `registration` event is the canonical source
        // of the FCM token on Android (no Firebase swizzle conflict to work
        // around). Listener is gated by platform so iOS doesn't attach a
        // never-firing handler that future readers might mistake for the
        // active token path. Both platforms ultimately call registerToken().
        let regHandle: { remove: () => Promise<void> } | null = null;
        if (Capacitor.getPlatform() === "android") {
          regHandle = await PushNotifications.addListener(
            "registration",
            (token) => {
              console.log(
                `[fcm] android registration event fired tokenLen=${token?.value?.length ?? 0}`,
              );
              void registerToken(token?.value || "");
            },
          );
          console.log("[fcm] android registration listener attached");
        }

        const errHandle = await PushNotifications.addListener(
          "registrationError",
          (err) => {
            console.warn("[fcm] registration error:", err);
          },
        );

        // Tap action — fired when the user opens a notification from the
        // tray / lock screen. Navigation goes to the SMRT Tasks list; v1
        // does not deep-link to a specific task.
        const tapHandle = await PushNotifications.addListener(
          "pushNotificationActionPerformed",
          () => {
            navigate(TASKS_ROUTE);
          },
        );

        console.log("[fcm] calling PushNotifications.register()");
        await PushNotifications.register();
        console.log("[fcm] PushNotifications.register() resolved");

        cleanup = () => {
          window.removeEventListener("asksmart-fcm-token", onFcmToken);
          regHandle?.remove();
          errHandle.remove();
          tapHandle.remove();
        };
      } catch (err) {
        console.warn("[fcm] setup failed:", err);
      }
    };

    setup();

    return () => {
      if (cleanup) cleanup();
    };
  }, [enabled, navigate]);
}
